import { Controller } from "@hotwired/stimulus"
import { afterOnboarding } from "../lib/onboarding"
import { pageCovered } from "../lib/overlay"

const SHOW_DELAY_MS = 700
const MODAL_RETRY_MS = 1500
const EDGE_PX = 12
const GAP_PX = 10

function seen(key) {
  try { return localStorage.getItem(`coachmark:${key}`) === "1" } catch { return true }
}

function markSeen(key) {
  try { localStorage.setItem(`coachmark:${key}`, "1") } catch { /* private mode: shows again next time */ }
}

// UI::CoachmarkComponent: waits for onboarding, for its anchor to sit in the upper part of the screen and for nothing
// to cover the page, then pops a tip next to the anchor (below it, or above when the fixed action bar would hide it).
// It counts as seen once it is actually on screen; 知道了, using the anchor or leaving the page (before-cache) closes it.
export default class extends Controller {
  static values = { key: String, anchor: String }

  connect() {
    if (seen(this.keyValue)) return
    this.cancelOnboarding = afterOnboarding(() => this.watch())
  }

  disconnect() {
    this.cancelOnboarding?.()
    this.observer?.disconnect()
    clearTimeout(this.timer)
    this.teardown()
  }

  watch() {
    this.target = document.querySelector(this.anchorValue)
    if (!this.target) return
    // Bottom margin keeps the trigger out of the strip behind the fixed action bar.
    this.observer = new IntersectionObserver(([ entry ]) => {
      if (entry.isIntersecting) {
        this.observer.disconnect()
        this.timer = setTimeout(() => this.show(), SHOW_DELAY_MS)
      }
    }, { rootMargin: "0px 0px -30% 0px", threshold: 0.5 })
    this.observer.observe(this.target)
  }

  show() {
    if (pageCovered()) {
      this.timer = setTimeout(() => this.show(), MODAL_RETRY_MS)
      return
    }
    this.element.hidden = false
    this.place()
    this.element.classList.add("is-shown")
    this.target.classList.add("coachmark-target")
    markSeen(this.keyValue)

    this.onScroll = () => {
      if (this.frame) return
      this.frame = requestAnimationFrame(() => { this.frame = null; this.place() })
    }
    this.onAnchorUse = () => this.dismiss()
    window.addEventListener("scroll", this.onScroll, { passive: true })
    window.addEventListener("resize", this.onScroll)
    this.target.addEventListener("pointerdown", this.onAnchorUse, { once: true })
  }

  place() {
    const rect = this.target.getBoundingClientRect()
    const { offsetWidth: width, offsetHeight: height } = this.element
    const bar = document.querySelector(".wizard-action-bar")?.getBoundingClientRect().top ?? window.innerHeight
    const above = rect.bottom + GAP_PX + height > bar && rect.top - GAP_PX - height > 0
    const left = Math.min(Math.max(rect.left + rect.width / 2 - width / 2, EDGE_PX), window.innerWidth - width - EDGE_PX)

    this.element.classList.toggle("is-above", above)
    this.element.style.top = `${above ? rect.top - GAP_PX - height : rect.bottom + GAP_PX}px`
    this.element.style.left = `${left}px`
    this.element.style.setProperty("--arrow-x", `${rect.left + rect.width / 2 - left}px`)
  }

  dismiss() {
    this.element.classList.remove("is-shown")
    this.element.hidden = true
    this.teardown()
  }

  teardown() {
    if (this.frame) cancelAnimationFrame(this.frame)
    this.frame = null
    if (this.onScroll) {
      window.removeEventListener("scroll", this.onScroll)
      window.removeEventListener("resize", this.onScroll)
    }
    if (this.onAnchorUse) this.target?.removeEventListener("pointerdown", this.onAnchorUse)
    this.target?.classList.remove("coachmark-target")
  }
}
